import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Search } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { ApiKeyAdmin, PaginaApiKeys } from '../../api/tipos';
import {
  Abas,
  Aviso,
  BarraFerramentas,
  Botao,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Esqueleto,
  Etiqueta,
  Paginacao,
  Tabela,
  Td,
  Th,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora, tempoRelativo } from '../../util/formato';
import css from './ApiKeysAdmin.module.css';

const SITUACOES = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'ativas', rotulo: 'Em uso' },
  { valor: 'revogadas', rotulo: 'Revogadas' },
] as const;

type Situacao = (typeof SITUACOES)[number]['valor'];

const ATRASO_BUSCA_MS = 350;

/**
 * Chaves de API emitidas por todos os clientes.
 *
 * O segredo não aparece aqui e não existe forma de recuperá-lo: guardamos só o
 * hash. O que este painel oferece é o que de fato importa para o controle —
 * saber quais credenciais existem, quais ainda são usadas e poder cortar
 * qualquer uma na hora.
 */
export function ApiKeysAdmin() {
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [situacao, setSituacao] = useState<Situacao>('todas');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<PaginaApiKeys | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [revogando, setRevogando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const q = new URLSearchParams({
      pagina: String(pagina),
      porPagina: '25',
      situacao,
    });
    if (busca.trim()) q.set('busca', busca.trim());

    try {
      setErro(null);
      setDados(await api<PaginaApiKeys>(`/admin/api-keys?${q.toString()}`));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao listar as chaves.',
      );
    }
  }, [situacao, busca, pagina]);

  useEffect(() => {
    const t = window.setTimeout(() => void carregar(), ATRASO_BUSCA_MS);
    return () => clearTimeout(t);
  }, [carregar]);

  async function revogar(k: ApiKeyAdmin) {
    const confirmado = await confirmar({
      titulo: `Revogar a chave "${k.nome}"?`,
      mensagem: `A chave é de ${k.user.email}. Qualquer integração que a use para de autenticar imediatamente, e a chave não pode ser reativada — o cliente terá que emitir outra.`,
      confirmar: 'Revogar chave',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!confirmado) return;

    setErro(null);
    setRevogando(k.id);
    try {
      await api(`/admin/api-keys/${k.id}`, { metodo: 'DELETE' });
      notificar(`Chave "${k.nome}" revogada.`);
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Não foi possível revogar.',
      );
    } finally {
      setRevogando(null);
    }
  }

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Chaves de API"
        descricao="Credenciais máquina-a-máquina emitidas pelos clientes. O segredo nunca é exibido — só o hash dele existe no banco."
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Cartao padding={0}>
        <BarraFerramentas>
          <Crescer>
            <Campo
              rotulo="Buscar chaves"
              rotuloOculto
              type="search"
              icone={<Search />}
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar por cliente, e-mail, nome ou prefixo da chave"
            />
          </Crescer>
          <Abas
            rotulo="Situação da chave"
            opcoes={SITUACOES}
            atual={situacao}
            aoMudar={(v) => {
              setSituacao(v);
              setPagina(1);
            }}
          />
        </BarraFerramentas>

        {!dados ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : dados.itens.length === 0 ? (
          <Vazio
            icone={<KeyRound />}
            titulo="Nenhuma chave"
            descricao="Nenhum cliente emitiu credencial que bata com este filtro."
          />
        ) : (
          <>
            <Tabela rotulo="Chaves de API">
              <thead>
                <tr>
                  <Th>Chave</Th>
                  <Th>Cliente</Th>
                  <Th>Último uso</Th>
                  <Th>Criada</Th>
                  <Th>Situação</Th>
                  <Th encolher>
                    <span className="sr-only">Ações</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((k) => (
                  <tr key={k.id}>
                    <Td>
                      <Celula
                        principal={k.nome}
                        secundaria={<span className={css.prefixo}>bxk_{k.id}_…</span>}
                      />
                    </Td>
                    <Td>
                      <Celula principal={k.user.nome} secundaria={k.user.email} />
                    </Td>
                    <Td className={css.apoio}>
                      {k.ultimoUso ? tempoRelativo(k.ultimoUso) : 'Nunca usada'}
                    </Td>
                    <Td className={css.apoio}>{dataHora(k.createdAt)}</Td>
                    <Td>
                      <div className={css.etiquetas}>
                        {k.revogadaEm ? (
                          <Etiqueta tom="erro">Revogada</Etiqueta>
                        ) : k.expirada ? (
                          <Etiqueta tom="neutro">Expirada</Etiqueta>
                        ) : (
                          <Etiqueta tom="ok">Em uso</Etiqueta>
                        )}
                        {!k.user.ativo && (
                          <Etiqueta tom="alerta">Conta bloqueada</Etiqueta>
                        )}
                      </div>
                    </Td>
                    <Td encolher>
                      {!k.revogadaEm && (
                        <Botao
                          aparencia="perigo"
                          pequeno
                          carregando={revogando === k.id}
                          onClick={() => void revogar(k)}
                        >
                          Revogar
                        </Botao>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            <Paginacao
              total={dados.total}
              pagina={dados.pagina}
              porPagina={dados.porPagina}
              aoMudar={setPagina}
              rotulo={dados.total === 1 ? 'chave' : 'chaves'}
            />
          </>
        )}
      </Cartao>
    </Pagina>
  );
}
